import {
  AccessToken,
  DefaultTokenStore,
  IdToken,
  type Logger,
  type TokenStore,
} from "@meiden-connect/auth-frontend-sdk";
import {
  resolveStoredMeidenConnectAccessToken,
  withAuthOperationTimeout,
} from "@osakabe/auth-service-provider";
import type { SessionSnapshot } from "./types";

const silentLogger: Logger = {
  debug: () => undefined,
  error: () => undefined,
  info: () => undefined,
  warn: () => undefined,
};

interface TokenStoreLike
  extends Pick<TokenStore, "readTokenSet" | "readUserIds" | "deleteTokenSet"> {
  readonly close?: (() => Promise<void>) | undefined;
}

type TokenStoreFactory = (input: {
  readonly authServiceOrigin: string;
  readonly serviceOrigin: string;
}) => TokenStoreLike;

let createTokenStore: TokenStoreFactory = ({ authServiceOrigin, serviceOrigin }) =>
  new DefaultTokenStore({
    authServiceOrigin,
    logger: silentLogger,
    serviceOrigin,
  });

/**
 * SDK の `MeidenConnectCredentialsProvider` をマウントする前に、IndexedDB に保存済みの
 * トークンから SessionSnapshot を組み立てる。
 *
 * これにより、アクセストークンの期限切れ判定や refresh が必要な場合でも、メイン UI を
 * Suspense でブロックせず、キャッシュ済みのユーザー情報で先行描画できる。
 *
 * 保存済みアクセストークンが期限切れであっても、refresh token が有効なら直接 refresh
 * 経路で再取得を試みる。これに失敗 (refresh token も期限切れ等) した場合のみ Snapshot
 * を破棄し、可視 SDK Provider 経路でサインインを促す。
 */
export async function restoreSessionSnapshotFromMeidenConnect(input: {
  readonly authServiceOrigin: string;
  readonly serviceOrigin: string;
}): Promise<SessionSnapshot | undefined> {
  const tokenStore = createTokenStore({
    authServiceOrigin: input.authServiceOrigin,
    serviceOrigin: input.serviceOrigin,
  });

  try {
    const userIds = await withAuthOperationTimeout(
      "session-bootstrap-read-user-ids",
      tokenStore.readUserIds()
    );
    if (userIds.length === 0) {
      return undefined;
    }

    // SDK の `TokenStore.readUserIds()` は順序が実装定義のため、先頭要素を最新利用者と
    // 決めつけると複数アカウントが残っている環境で誤ったユーザーの SessionSnapshot を復元する
    // 恐れがある。
    //
    // 1) まず全 userId の tokenSet を読み、それぞれの idToken `iat` を取得する (refresh は走らせない)。
    // 2) アクセストークンが期限内のものがあれば、その中で最新の iat の tokenSet を採用する。
    // 3) 期限切れしかない場合は、最新 iat の userId に対してのみ refresh を 1 回だけ試みる。
    //    古い userId が複数残っていても、refresh の直列実行で起動が遅延するのを避けるための
    //    制約。最新 iat の refresh に失敗した場合は復元しない (= 可視 SDK Provider 経路に任せる)。
    const candidates: ReadonlyArray<{
      readonly userId: string;
      readonly tokenSet: { accessToken: string; idToken: string; refreshToken: string };
      readonly idTokenIat: number;
      readonly accessTokenAlive: boolean;
    }> = (
      await Promise.all(
        userIds.map(async (userId) => {
          const tokenSet = await withAuthOperationTimeout(
            "session-bootstrap-read-token-set",
            tokenStore.readTokenSet(userId, [input.serviceOrigin])
          ).catch(() => undefined);
          if (tokenSet === undefined) {
            return undefined;
          }
          return {
            userId,
            tokenSet,
            idTokenIat: readIdTokenIat(tokenSet.idToken),
            accessTokenAlive: isAccessTokenAlive(tokenSet.accessToken),
          };
        })
      )
    ).filter((entry): entry is Exclude<typeof entry, undefined> => entry !== undefined);
    if (candidates.length === 0) {
      return undefined;
    }

    let bestTokenSet:
      | { readonly idTokenIat: number; readonly tokenSet: { accessToken: string; idToken: string; refreshToken: string } }
      | undefined;
    const aliveCandidates = candidates.filter((entry) => entry.accessTokenAlive);
    if (aliveCandidates.length > 0) {
      // 期限内アクセストークンを持つ候補の中から、最新 iat の 1 件を採用する。
      const latestAlive = aliveCandidates.reduce((max, entry) =>
        entry.idTokenIat > max.idTokenIat ? entry : max
      );
      bestTokenSet = { idTokenIat: latestAlive.idTokenIat, tokenSet: latestAlive.tokenSet };
    } else {
      // 期限切れしか無い場合は、最新 iat の userId だけ refresh を試みる対象として 1 件選ぶ。
      const latestExpired = candidates.reduce((max, entry) =>
        entry.idTokenIat > max.idTokenIat ? entry : max
      );
      const refreshedAccessToken = await resolveStoredMeidenConnectAccessToken({
        authServiceOrigin: input.authServiceOrigin,
        forceRefresh: true,
        serviceOrigin: input.serviceOrigin,
        serviceOrigins: [input.serviceOrigin],
        viewerUserId: latestExpired.userId,
      }).catch(() => undefined);
      if (refreshedAccessToken !== undefined) {
        const refreshedTokenSet = await withAuthOperationTimeout(
          "session-bootstrap-read-refreshed-token-set",
          tokenStore.readTokenSet(latestExpired.userId, [input.serviceOrigin])
        ).catch(() => undefined);
        if (
          refreshedTokenSet !== undefined &&
          isAccessTokenAlive(refreshedTokenSet.accessToken)
        ) {
          bestTokenSet = {
            idTokenIat: readIdTokenIat(refreshedTokenSet.idToken),
            tokenSet: refreshedTokenSet,
          };
        }
      }
    }
    if (bestTokenSet === undefined) {
      return undefined;
    }

    const idToken = new IdToken(bestTokenSet.tokenSet.idToken);
    const accessToken = new AccessToken(bestTokenSet.tokenSet.accessToken, input.serviceOrigin);

    const tokenPermissions = readPermissionIds(accessToken.globalPermissionIds);
    const idTokenPermissions = readPermissionIds(
      (idToken as { readonly globalPermissionIds?: unknown }).globalPermissionIds
    );
    const mergedPermissions = [...new Set([...tokenPermissions, ...idTokenPermissions])];

    return {
      userId: idToken.userId,
      name: idToken.name,
      email: typeof idToken.email === "string" ? idToken.email : undefined,
      // SDK は IdToken に `department` を載せることがあるため、それを `affiliation` として採用する。
      affiliation: readAffiliationFromIdToken(idToken),
      avatarImageUrl:
        typeof idToken.iconUrl === "string" && idToken.iconUrl.length > 0
          ? idToken.iconUrl
          : undefined,
      source: "meiden-connect",
      globalPermissionIds: mergedPermissions,
    };
  } catch {
    return undefined;
  } finally {
    const closePromise = tokenStore.close?.();
    if (closePromise !== undefined) {
      await withAuthOperationTimeout(
        "session-bootstrap-token-store-close",
        closePromise,
        1_000
      ).catch(() => undefined);
    }
  }
}

/** ID トークンの `iat` (issued at, epoch seconds) を取り出す。読めない場合は 0 を返す。 */
function readIdTokenIat(idToken: string): number {
  try {
    const segments = idToken.split(".");
    const payloadSegment = segments[1];
    if (payloadSegment === undefined) {
      return 0;
    }
    const normalizedSegment = payloadSegment.replaceAll("-", "+").replaceAll("_", "/");
    const paddedSegment = normalizedSegment.padEnd(
      Math.ceil(normalizedSegment.length / 4) * 4,
      "="
    );
    const parsed = JSON.parse(atob(paddedSegment));
    const iat =
      typeof parsed.iat === "number"
        ? parsed.iat
        : typeof parsed.iat === "string"
          ? Number(parsed.iat)
          : 0;
    return typeof iat === "number" && Number.isFinite(iat) ? iat : 0;
  } catch {
    return 0;
  }
}

/**
 * IndexedDB の MEIDEN CONNECT token store から、対象 origin に保存されている全 userId のトークンセットを削除する。
 *
 * SDK サブツリーが停止していて `user.signOut()` を呼べない場合の best-effort なログアウト経路から使用する想定。
 * これを忘れると localStorage / React state だけクリアしても、後で SDK が再マウントされたタイミングで
 * IndexedDB の残存トークンから自動再ログインしてしまう。
 */
export async function purgeAllMeidenConnectTokens(input: {
  readonly authServiceOrigin: string;
  readonly serviceOrigin: string;
}): Promise<void> {
  const tokenStore = createTokenStore({
    authServiceOrigin: input.authServiceOrigin,
    serviceOrigin: input.serviceOrigin,
  });
  try {
    const userIds = await withAuthOperationTimeout(
      "session-bootstrap-purge-read-user-ids",
      tokenStore.readUserIds()
    );
    for (const userId of userIds) {
      await withAuthOperationTimeout(
        "session-bootstrap-purge-delete-token-set",
        tokenStore.deleteTokenSet(userId)
      ).catch(() => undefined);
    }
  } catch {
    // 失敗しても利用者の明示的なログアウト操作を阻害しない。
  } finally {
    const closePromise = tokenStore.close?.();
    if (closePromise !== undefined) {
      await withAuthOperationTimeout(
        "session-bootstrap-purge-token-store-close",
        closePromise,
        1_000
      ).catch(() => undefined);
    }
  }
}

/** アクセストークンが現時点でまだ有効か (30 秒の猶予つき) を JWT パース無しで簡易判定する */
function isAccessTokenAlive(accessToken: string | undefined): boolean {
  if (typeof accessToken !== "string" || accessToken.length === 0) {
    return false;
  }

  try {
    const segments = accessToken.split(".");
    const payloadSegment = segments[1];
    if (payloadSegment === undefined) {
      return false;
    }

    const normalizedSegment = payloadSegment.replaceAll("-", "+").replaceAll("_", "/");
    const paddedSegment = normalizedSegment.padEnd(
      Math.ceil(normalizedSegment.length / 4) * 4,
      "="
    );
    const parsed = JSON.parse(atob(paddedSegment));
    const exp =
      typeof parsed.exp === "number"
        ? parsed.exp
        : typeof parsed.exp === "string"
          ? Number(parsed.exp)
          : undefined;

    if (typeof exp !== "number" || !Number.isFinite(exp)) {
      return false;
    }

    return exp > Date.now() / 1000 + 30;
  } catch {
    return false;
  }
}

function readPermissionIds(value: unknown): readonly string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((entry): entry is string => typeof entry === "string");
}

function readAffiliationFromIdToken(idToken: IdToken): string | undefined {
  // IdToken は SDK 内部で `department` 等のオプショナル項目を持つことがある。
  // 公開型に依存しすぎないよう unknown 経由で安全に読み出す。
  const candidate = idToken as unknown as {
    readonly department?: unknown;
    readonly affiliation?: unknown;
  };
  const department =
    typeof candidate.department === "string" && candidate.department.length > 0
      ? candidate.department
      : undefined;
  if (department !== undefined) {
    return department;
  }
  const affiliation =
    typeof candidate.affiliation === "string" && candidate.affiliation.length > 0
      ? candidate.affiliation
      : undefined;
  return affiliation;
}

/**
 * テスト用にトークンストア生成処理を差し替える。
 */
export function setSessionBootstrapTokenStoreFactoryForTest(
  factory?: TokenStoreFactory
): void {
  createTokenStore =
    factory ??
    (({ authServiceOrigin, serviceOrigin }) =>
      new DefaultTokenStore({
        authServiceOrigin,
        logger: silentLogger,
        serviceOrigin,
      }));
}
